import React, { useState, createRef, useEffect } from "react";
import * as queryString from "query-string";
import { useLocation, Navigate } from "react-router-dom";
import routes from "../Apps/routes";
import { Captcha, useCaptcha } from "../common/components/captcha";

import { Alert } from "@codegouvfr/react-dsfr/Alert";
import { Button } from "@codegouvfr/react-dsfr/Button";
import { Input } from "@codegouvfr/react-dsfr/Input";
import { PasswordInput } from "@codegouvfr/react-dsfr/blocks/PasswordInput";
import { Loader } from "../Apps/common/Components";

import styles from "./Login.module.scss";
import { envConfig } from "../common/envConfig";

function getErrorMessage(errorCode: string): {
  description: string | React.ReactElement;
  title?: string;
  severity?: "error" | "info" | "success" | "warning";
} {
  if (errorCode === "NOT_ACTIVATED") {
    return {
      title: "Votre compte n'est pas activé",
      description: (
        <>
          Suite à la création de votre compte, un courriel d’activation vous a
          été envoyé.
          <ul>
            <li>- Vérifiez dans les indésirables (SPAM).</li>
            <li>
              - Vous ne l'avez pas reçu ?{" "}
              <a
                href={routes.resendActivationEmail}
                className="fr-link force-underline-link"
              >
                Renvoyer le courriel d'activation
              </a>
            </li>
          </ul>
        </>
      )
    };
  }
  if (errorCode === "INVALID_USER_OR_PASSWORD_NEEDS_CAPTCHA") {
    return {
      description:
        "Courriel ou mot de passe incorrect - Veuillez également compléter le test anti-robots"
    };
  }
  if (errorCode === "INVALID_CAPTCHA") {
    return {
      description: "Le test anti-robots est incorrect"
    };
  }

  if (errorCode === "TOTP_TIMEOUT_OR_MISSING_SESSION") {
    return {
      description: "Votre session a expiré. Veuillez vous reconnecter."
    };
  }

  if (errorCode === "MFA_RESET_IN_PROGRESS") {
    return {
      title: "MFA en cours de réinitialisation",
      severity: "info",
      description: (
        <>
          La réinitialisation du MFA a été demandée pour ce compte. Merci de
          vérifier vos mails afin de finaliser la procédure. Si vous n'êtes pas
          à l'origine de cette demande, contactez notre support via l'Assistance
          Trackdéchets.{" "}
          <a
            href="https://assistance.trackdechets.beta.gouv.fr/"
            className="fr-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            Contacter l'assistance
          </a>
        </>
      )
    };
  }

  if (errorCode === "MFA_RESET_DONE_RECONFIG_REQUIRED") {
    return {
      title: "Double authentification réinitialisée",
      severity: "warning",
      description: (
        <>
          Votre double authentification a été réinitialisée. Pour vous
          reconnecter, utilisez le lien de reconfiguration reçu par e-mail.
          <br />
          Si vous n'avez pas reçu cet e-mail, s'il est expiré ou déjà utilisé,
          contactez notre support.{" "}
          <a
            href="https://assistance.trackdechets.beta.gouv.fr"
            className="fr-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            Contacter l'Assistance Trackdéchets
          </a>
        </>
      )
    };
  }

  return {
    description: "Courriel ou mot de passe incorrect"
  };
}

function displayCaptcha(errorCode?: string) {
  if (!errorCode) {
    return false;
  }
  return ["INVALID_USER_OR_PASSWORD_NEEDS_CAPTCHA", "INVALID_CAPTCHA"].includes(
    errorCode
  );
}

export default function Login() {
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [captchaInput, setCaptchaInput] = useState("");

  const queries = queryString.parse(location.search);

  // fill email field from passed state
  useEffect(() => {
    if (location.state?.username) {
      setEmail(location.state.username);
    }
  }, [setEmail, location]);

  const { captchaLoading, captchaError, captchaData, refetchCaptcha } =
    useCaptcha(displayCaptcha(location?.state?.errorCode));

  if (captchaError) {
    return <div>{captchaError}</div>;
  }
  if (captchaLoading) {
    return <Loader />;
  }

  const formRef = createRef<HTMLFormElement>();
  const { VITE_API_ENDPOINT } = envConfig;

  const createdWithSuccess = queries.signup === "complete";

  if (queries.errorCode || queries.returnTo) {
    const { errorCode, returnTo, username } = queries;
    const state = {
      ...(queries.errorCode ? { errorCode, username } : {}),
      ...(!!returnTo ? { returnTo } : {})
    };

    return <Navigate to={{ pathname: routes.login }} state={state} />;
  }

  const { returnTo, errorCode, username = "" } = location.state || {};

  const showCaptcha = displayCaptcha(errorCode);

  const errorObject = errorCode ? getErrorMessage(errorCode) : null;

  const alert = errorObject ? (
    <div className="fr-grid-row fr-mb-2w">
      <Alert
        title={errorObject.title ?? "Erreur"}
        description={errorObject.description}
        severity={errorObject.severity ?? "error"}
      />
    </div>
  ) : null;

  const createdAlert = createdWithSuccess ? (
    <div className="fr-grid-row fr-mb-2w">
      <Alert
        title="Votre compte est créé !"
        description="Vous pouvez maintenant vous connecter puis créer ou rejoindre un établissement."
        severity="success"
      />
    </div>
  ) : null;

  const disconnectedAlert =
    queries["session"] === "expired" ? (
      <div className="fr-grid-row fr-mb-2w">
        <Alert
          title="Votre session a expiré"
          description="Vous avez été déconnecté, veuillez vous connecter à nouveau."
          severity="warning"
        />
      </div>
    ) : null;

  return (
    <div className={styles.onboardingWrapper}>
      <form
        ref={formRef}
        action={`${VITE_API_ENDPOINT}/login`}
        method="post"
        name="login"
      >
        <div className={`fr-container fr-pt-10w ${styles.centralContainer}`}>
          {createdAlert}
          {disconnectedAlert}
          {alert}
          <div className="fr-grid-row fr-grid-row--center fr-mb-2w">
            <div className="fr-col fr-m-auto">
              <h1 className="fr-h3 fr-mb-3w">Se connecter</h1>

              <Input
                nativeInputProps={{
                  value: email || username,
                  name: "email",
                  required: true,
                  onChange: e => setEmail(e.target.value)
                }}
                label="Courriel"
              />
              <PasswordInput
                nativeInputProps={{
                  name: "password",
                  value: password,
                  required: true,
                  onChange: e => setPassword(e.target.value)
                }}
                label="Mot de passe"
              />
              {returnTo && (
                <input type="hidden" name="returnTo" value={returnTo} />
              )}

              {showCaptcha && !!captchaData?.token && !!captchaData?.img && (
                <>
                  <input
                    type="hidden"
                    value={captchaData.token}
                    name="captchaToken"
                  />

                  <Captcha
                    setCaptchaInput={setCaptchaInput}
                    captchaImg={captchaData.img}
                    captchaInput={captchaInput}
                    captchaToken={captchaData.token}
                    refetch={refetchCaptcha}
                    narrow={true}
                  />
                </>
              )}
              <a
                href={routes.passwordResetRequest}
                className="fr-link force-underline-link"
              >
                Mot de passe oublié ?
              </a>
            </div>
          </div>
          <div className="fr-grid-row fr-grid-row">
            <div className={`fr-col ${styles.resetFlexCol}`}>
              <Button size="medium" nativeButtonProps={{ type: "submit" }}>
                Se connecter
              </Button>
            </div>
          </div>
          <div className="fr-grid-row fr-pt-3w">
            <div className="fr-col">
              <p className="fr-text--md">
                Vous n'avez pas encore de compte ?{" "}
                <a
                  href={routes.signup.index}
                  className="fr-link force-underline-link"
                >
                  Inscrivez-vous maintenant
                </a>
              </p>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
